import React, { useState } from "react"
import styled from "styled-components"
import { useDebouncedCallback } from "use-debounce"
import PropTypes from "prop-types"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTimes } from "@fortawesome/free-solid-svg-icons"
import { useDispatch, useSelector } from "react-redux"
import {
  DropdownContainer,
  DropdownMenu,
  DropdownMenuItem,
} from "../../../ui/DropdownMenu"
import {
  clearMatches,
  fetchRSOs,
  selectRSOsByContext,
  selectRSOsByIds,
} from "../slice"
import Input from "../../../ui/Input"
import RSOType from "../RSOType"
import LoadingSpinnerInline from "../../../ui/LoadingSpinnerInline"
const { DateTime } = require("luxon")

const ClearBtn = styled.button`
  border: none;
  background: none;
  color: ${(props) => props.theme.colours.error};
`

const LoadingContainer = styled.div`
  border: none;
  background: none;
  color: ${(props) => props.theme.colours.active};
`

const Container = styled(DropdownContainer)`
  display: flex;
  width: 100%;
  ${Input} {
    flex: auto;
    width: 100%;
  }
  ${ClearBtn} {
    flex: none;
    font-size: 1.5rem;
    padding: 0 0.5rem;
  }
  ${LoadingContainer} {
    flex: none;
    font-size: 1.5rem;
    padding: 0 0.5rem;
  }
`

const Label = styled.div`
  flex: auto;
`

const Icon = styled.div`
  flex: none;
`

/**
 * A filter for RSO's
 * It supports both object_name and object_id (norad id)
 */
const RSOFilter = ({
  filter = {},
  setFilter,
  textEntryDebounceMs = 600,
  active,
  name,
  placeholder = "name or norad id",
}) => {
  const dispatch = useDispatch()
  // The search term entered
  const [search, setSearch] = useState(filter["object_id"])
  // we get any matching RSO's
  const rsoMatches = useSelector((state) =>
    selectRSOsByContext(state, "search")
  )
  // We get the full objects from the ids that match
  const rsos = useSelector((state) =>
    selectRSOsByIds(state, rsoMatches?.ids || [])
  )

  // updateParentFilter calls the given `setFilter`
  // based on local state, i.e. text to search for.
  const updateParentFilter = (choice) => {
    const field = name ? name : "object_id"
    filter = {
      ...filter,
      [field]: choice,
    }
    setFilter(filter)
  }

  // Query the backend
  const searchRSOs = () => {
    dispatch(
      fetchRSOs({
        filter: { name_cnts: search },
        page: { offset: 0, limit: 200 },
        context: "search",
        requestedAt: DateTime.utc().toISO(),
      })
    )
  }

  // But only call `setFilter` debounced, so that we don't
  // cause re-renders and API calls with every keystroke.
  const updateSearchRSOsDebounced = useDebouncedCallback(
    searchRSOs,
    textEntryDebounceMs
  )

  // Called when the search value changes
  const changed = (e) => {
    // handle case where uses backspaces
    if (e.target.value === "") {
      cleared()
    }
    setSearch(e.target.value)
    updateSearchRSOsDebounced()
  }

  // Called when there is an object selected from the search results
  const selected = ({ id }) => {
    setSearch(id)
    updateParentFilter(id)
    dispatch(clearMatches("search"))
  }

  const cleared = () => {
    setSearch("") // Immediate local state
    filter["object_id"] = ""
    setFilter(filter) // Parent filter
  }

  // TODO: On enter, update immediately
  const keyPressed = (e) => {
    if (e.key === "Enter") {
      updateParentFilter()
    }
  }

  return (
    <Container>
      <Input
        type="text"
        value={search || ""}
        onKeyPress={keyPressed}
        onChange={changed}
        placeholder={placeholder || "name or norad id"}
        active={active}
      />
      {search && (
        <ClearBtn onClick={cleared} title="Clear filter">
          <FontAwesomeIcon icon={faTimes} />
        </ClearBtn>
      )}
      {rsoMatches?._loadStatus === "loading" && <LoadingSpinnerInline />}
      <div>
        {rsoMatches?._loadStatus === "loading" && <div>…</div>}
        {rsoMatches?._loadStatus !== "loading" &&
          search !== "" &&
          rsos.length > 0 && (
            <DropdownMenu>
              {rsos.map((rso) => (
                <DropdownMenuItem
                  key={rso.id}
                  onClick={() =>
                    selected({ id: rso.id, name: rso.object_name })
                  }
                >
                  <Label>
                    {rso.object_name} - {rso.id}
                  </Label>
                  <Icon>
                    <RSOType type={rso.object_type} printName={false} />
                  </Icon>
                </DropdownMenuItem>
              ))}
            </DropdownMenu>
          )}
      </div>
    </Container>
  )
}

RSOFilter.propTypes = {
  name: PropTypes.string, // The field name in the filter
  placeholder: PropTypes.string, // Optionally overrides the displayed placeholder text
  filter: PropTypes.object.isRequired,
  setFilter: PropTypes.func.isRequired,
  textEntryDebounceMs: PropTypes.number,
  active: PropTypes.bool, // Indication from parent that filter is applied
}

export default RSOFilter
